import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ThankYou.module.scss';
import { PageWrapper } from '../../components/layout';
import { TypingEffect } from '../../components/ui';
import { useAppDispatch, useAppSelector } from '../../store';
import { setUserEmail } from '../../store/slices/userSlice';
import { resetQuiz } from '../../store/slices/quizSlice';
import { resetFormData } from '../../store/slices/formDataSlice';
import { setCcFormCompleted, showToast } from '../../store/slices/uiSlice';
import { getPdf } from '../../api/reimbursement';
import { robotMessages } from '../../config/quizQuestions';

import RobotIcon from '../../assets/icons/robotWithCard.svg';
import VectorIcon from '../../assets/icons/vectorBottom.svg';

const ThankYou: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isSendingPdf, setIsSendingPdf] = useState(false);

  const email = useAppSelector((state) => state.user.email);
  const sessionEmail = sessionStorage.getItem('email');
  const ref = sessionStorage.getItem('ref');

  // Reset state and start new application
  const handleNewApplication = useCallback(() => {
    // Clear session storage
    sessionStorage.removeItem('ref');
    sessionStorage.removeItem('email');

    // Reset Redux state
    dispatch(setUserEmail(''));
    dispatch(resetQuiz());
    dispatch(resetFormData());
    dispatch(setCcFormCompleted(false));

    // Navigate to home
    navigate('/');
  }, [dispatch, navigate]);

  // Handle back navigation
  useEffect(() => {
    const handlePopState = () => {
      handleNewApplication();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [handleNewApplication]);

  // Resend PDF to email
  const handleResendPdf = async () => {
    if (!ref) {
      dispatch(
        showToast({
          type: 'error',
          body: 'Keine Referenz gefunden.',
        })
      );
      return;
    }

    setIsSendingPdf(true);

    try {
      await getPdf({ ref });
      dispatch(
        showToast({
          type: 'success',
          body: 'Die PDF wurde erfolgreich an Ihre E-Mail gesendet!',
        })
      );
    } catch (error) {
      console.error('Error sending PDF:', error);
      dispatch(
        showToast({
          type: 'error',
          body: 'Fehler beim Senden der PDF. Bitte versuchen Sie es später erneut.',
        })
      );
    } finally {
      setIsSendingPdf(false);
    }
  };

  return (
    <PageWrapper>
      <div className={styles.thankYou}>
        <div className={styles.content}>
          {/* Robot message */}
          <div className={styles.messageSection}>
            <div className={styles.messageRobot}>
              <p>
                <TypingEffect text={robotMessages.thankYou} speed={30} />
              </p>
              <img src={VectorIcon} alt="" className={styles.vector} />
            </div>
          </div>

          {/* Robot image */}
          <div className={styles.robotContainer}>
            <img src={RobotIcon} alt="FLEXX-i Robot" className={styles.robot} />
          </div>

          {/* Actions */}
          <div className={styles.actionsSection}>
            <div className={styles.infoBox}>
              <h3>Was passiert als nächstes?</h3>
              <ul>
                <li>Wir prüfen Ihren Antrag in den nächsten 2-3 Werktagen</li>
                <li>
                  Sie erhalten eine Bestätigung per E-Mail an{' '}
                  <strong>{email || sessionEmail}</strong>
                </li>
                <li>Bei Fragen melden wir uns bei Ihnen</li>
              </ul>
            </div>

            <div className={styles.buttonGroup}>
              <button
                className={styles.pdfButton}
                onClick={handleResendPdf}
                disabled={isSendingPdf}
              >
                {isSendingPdf ? 'Wird gesendet...' : 'PDF erneut per E-Mail senden'}
              </button>

              <button className={styles.newButton} onClick={handleNewApplication}>
                Neuen Antrag starten
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default ThankYou;
